import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { countContactsFilters } from '../../../hooks/useContactsFilterCount';
import { AIFilters } from '../../molecules/filters/aiFilters';
import KeywordFilter from '../../atoms/KeywordFilter';
import { Accordion } from './components/accordion';
import { createGroups } from './groups';

const AccordionContainer = styled.div`
  width: 100%;
  padding-bottom: 16px;
  height: 100%;

  && label {
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.main};
  }
`;

export const Filters = ({
  sharedList,
  filters,
  setFilters,
  extendedMode,
  isTalentNetworkEnabled,
  showListFilters,
  showTalentNetworkFilters,
}) => {
  const groups = React.useMemo(
    () =>
      createGroups({
        sharedList,
        filters,
        extendedMode,
        isTalentNetworkEnabled,
        showListFilters,
        showTalentNetworkFilters,
      }).reduce((acc, { id: groupId, title, show, filters: groupFilters }) => {
        if (show) {
          const filtersToCount = {};
          const currentGroup = {
            id: groupId,
            title,
            content: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {groupFilters.reduce(
                  (acc2, { id: filterId, Component, props, show: showFilter, additionalCountKeys }) => {
                    if (showFilter) {
                      acc2.push(
                        <Component key={filterId} {...props} value={filters[filterId]} setFilters={setFilters} />,
                      );

                      if (filters[filterId]) {
                        filtersToCount[filterId] = filters[filterId];
                      }

                      // Include additional filter keys for counting (e.g., for compound filters like funding stage)
                      if (additionalCountKeys) {
                        additionalCountKeys.forEach((key) => {
                          if (filters[key]) {
                            filtersToCount[key] = filters[key];
                          }
                        });
                      }
                    }

                    return acc2;
                  },
                  [],
                )}
              </div>
            ),
          };

          const count = countContactsFilters(filtersToCount);
          if (count > 0) {
            currentGroup.badge = `${count}`;
          }

          acc.push(currentGroup);
        }

        return acc;
      }, []),
    [sharedList, filters, extendedMode, isTalentNetworkEnabled, showListFilters, showTalentNetworkFilters, setFilters],
  );

  return (
    <section
      style={{
        display: 'flex',
        flexGrow: 1,
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: '12px',
        position: 'relative',
        height: '100%',
      }}
    >
      {showListFilters && (
        <div style={{ width: '100%' }} data-testid="contact-filters-ai-filters">
          <AIFilters value={filters.aiFilters} setFilters={setFilters} />
        </div>
      )}
      <div style={{ width: '100%' }} data-testid="contact-filters-keywords">
        <KeywordFilter value={filters.keywords} onChange={(value) => setFilters({ keywords: value })} />
      </div>
      <AccordionContainer>
        <Accordion items={groups} />
      </AccordionContainer>
    </section>
  );
};

Filters.propTypes = {
  sharedList: PropTypes.object,
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  extendedMode: PropTypes.bool,
  isTalentNetworkEnabled: PropTypes.bool,
  showListFilters: PropTypes.bool.isRequired,
  showTalentNetworkFilters: PropTypes.bool,
};

Filters.defaultProps = {
  sharedList: null,
  extendedMode: false,
  isTalentNetworkEnabled: false,
  showTalentNetworkFilters: false,
};
